import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import {
  SET_EMAIL_MESSAGE,
  SET_EMAIL_SUBJECT,
  SET_EMAIL_SEND_TO,
  // TOGGLE_ADDRESS_IN_SUBJECT,
  TOGGLE_IS_PAID,
  TOGGLE_BOTH,
  TOGGLE_REVIEW,
  SET_EMAIL_ATTACHMENTS,
  SELECT_WORKFLOW,
  SET_EMAIL_SENT_STATUS,
} from "../reducers/pdfReducer";
import { dateTimeFormatter } from "../helperFunctions/dateFormatters";
import { getWorkflowsForType } from "../helperFunctions/workflows";
import { styleMessageText } from "../helperFunctions/emailPreviewHtml";
import RichTemplateEditor from "./RichTemplateEditor";

function truncate(text = "", maxLength) {
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

// Renders pdf.message the same way Settings > Templates renders a template while it's
// being edited - shorthand markup (**bold** etc.) turned into real formatting, instead of
// a plain <textarea> showing those markers and any saved-template HTML tags as literal
// text. Kept deliberately uncontrolled (like RichTemplateEditor itself): only reseeds
// (via a remount, so the cursor is never fought mid-edit) when `message` changes for a
// reason other than this box's own last edit - e.g. switching workflow/type recomputes
// the whole message from its template.
function MessageEditor({ message, onChange }) {
  const lastEmittedRef = useRef(message);
  const [seedHtml, setSeedHtml] = useState(() => styleMessageText(message));
  const [seedVersion, setSeedVersion] = useState(0);

  useEffect(() => {
    if (message !== lastEmittedRef.current) {
      lastEmittedRef.current = message;
      setSeedHtml(styleMessageText(message));
      setSeedVersion((v) => v + 1);
    }
  }, [message]);

  function handleChange(html) {
    lastEmittedRef.current = html;
    onChange(html);
  }

  return (
    <RichTemplateEditor key={seedVersion} id="message" initialHtml={seedHtml} onChange={handleChange} />
  );
}

function PdfEmailBox(props) {
  let {
    pdf,
    dispatch,
    timesheet,
    totalInvoiceCost,
    addressLine,
    sendPdf,
    createDraft,
    findQuoteThread,
    fetchBccAddress,
    threadCandidates,
    selectedThreadIndex,
    onSelectThread,
    aiSuggestedIndex,
    aiSuggestedReason,
    aiAttempted,
    aiError,
    // <<<<<<<<<<<<<<<<<<,
  } = props;

  // Determine whether to show an email warning. Will show if client email is a hotmail or icloud address
  const showSpamWarning = timesheet?.email?.includes("hotmail") || timesheet?.email?.includes("icloud");

  let isInvoice = pdf.pdfType === "Invoice";

  // Bcc badge: the label ("Invoices"/"Quotes") is known immediately from pdfType, matching
  // the same naming as the preset buttons below - but the real address is only fetched
  // lazily, on hover, since it's a network call and most of the time nobody needs it. Resets
  // whenever pdfType changes so switching Quote/Invoice doesn't show a stale cached address.
  let bccLabel = pdf.pdfType === "Quote" ? "Quotes" : "Invoices";
  const [bccAddress, setBccAddress] = useState(null);
  const [bccLoading, setBccLoading] = useState(false);
  const [bccHovering, setBccHovering] = useState(false);

  useEffect(() => {
    setBccAddress(null);
  }, [pdf.pdfType]);

  async function handleBccHover() {
    setBccHovering(true);
    if (bccAddress || bccLoading) return;

    setBccLoading(true);
    let address = await fetchBccAddress();
    setBccAddress(address);
    setBccLoading(false);
  }

  // Attach-to-thread picker: first click on "Create Draft" looks up candidate Gmail
  // threads and opens the panel to confirm/override; second click actually creates
  // the draft with whatever's currently selected. Applies to quotes and invoices alike.
  const [threadPickerOpen, setThreadPickerOpen] = useState(false);
  const [findingThread, setFindingThread] = useState(false);
  const emailBarRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState(null);

  // The panel needs position: fixed (to escape .popovers' overflow: auto, which would
  // otherwise clip it), so it can't just be centered with CSS relative to the email bar -
  // that box's height varies with content (spam warning, invoice-only checkboxes, etc).
  // Measure it instead so "halfway up the bar" tracks its actual current height.
  useLayoutEffect(() => {
    if (!threadPickerOpen) return;

    function updatePosition() {
      if (window.innerWidth <= 985 || !emailBarRef.current) {
        setPanelPosition(null);
        return;
      }

      let rect = emailBarRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.top + rect.height / 2,
        right: window.innerWidth - rect.left + 15,
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [threadPickerOpen, threadCandidates, findingThread]);

  async function handleCreateDraftClick() {
    if (!threadPickerOpen) {
      if (pdf.sendTo?.length === 0) {
        dispatch({
          type: SET_EMAIL_SENT_STATUS,
          payload: { emailSentStatus: "No recipients", sending: false },
        });

        return;
      }

      setThreadPickerOpen(true);
      setFindingThread(true);
      await findQuoteThread(pdf.sendTo);
      setFindingThread(false);
      return;
    }

    setThreadPickerOpen(false);
    createDraft();
  }

  let sendToBtns = [
    {
      label: "Invoices",
      className: "append-to-email",
      payload: { appendEmail: "invoices4hes@gmail.com" },
    },
    {
      label: "Quotes",
      className: "append-to-email",
      payload: { appendEmail: "quotes4hes@gmail.com" },
    },
    {
      label: "HES",
      className: "append-to-email",
      payload: { appendEmail: "hesglasgow@gmail.com" },
    },
    {
      label: "Client",
      className: "append-to-email",
      payload: { appendEmail: timesheet?.email },
    },
    {
      label: "Clear",
      className: "clear-email",

      payload: { value: "" },
    },
  ];

  function setupAttachments(file) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (upload) => {
      console.log("file name:", file.name);
      dispatch({
        type: SET_EMAIL_ATTACHMENTS,
        payload: [
          {
            filename: file.name,
            // content: upload.target.result,
            path: upload.target.result,
            // contentType: file.type,
            // encoding: "base64",
          },
        ],
      });
    };
  }

  async function handleFile(e) {
    dispatch({ type: SET_EMAIL_ATTACHMENTS, payload: [] });

    const files = Array.from(e.target.files);

    files.forEach((file, i) => {
      setupAttachments(file, i);
    });
  }

  return (
    <div className="pdfEmailBar" ref={emailBarRef}>
      <div className="toolLine statusLine">
        <label htmlFor="emailSentStatus">Status</label>
        {pdf.draftUrl ? (
          <a
            className="draftCreatedLink"
            aria-label="email-send-status"
            href={pdf.draftUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {pdf.emailSentStatus}
          </a>
        ) : (
          <p aria-label="email-send-status">{pdf.emailSentStatus}</p>
        )}
        {!pdf.both && (
          <div className="workflowControls">
            <select
              className="workflowSelect"
              value={pdf.workflowKey}
              disabled={pdf.sending}
              onChange={(e) =>
                dispatch({
                  type: SELECT_WORKFLOW,
                  payload: {
                    workflowKey: e.target.value,
                    amount: totalInvoiceCost,
                    addressLine,
                  },
                })
              }
            >
              {getWorkflowsForType(pdf.pdfType).map((workflow) => (
                <option key={workflow.key} value={workflow.key}>
                  {workflow.label}
                </option>
              ))}
            </select>
            <button className="sendBtn" disabled={pdf.sending} onClick={handleCreateDraftClick}>
              {threadPickerOpen ? "Confirm Draft" : "Create Draft"}
            </button>
          </div>
        )}
      </div>

      <div className="toolLine">
        <label htmlFor="emailSubject">Subject</label>
        <div className="subject-input-box">
          <textarea
            type="text"
            id="emailSubject"
            name="emailSubject"
            rows={2}
            autoComplete="off"
            onChange={(e) => dispatch({ type: SET_EMAIL_SUBJECT, payload: e.target.value })}
            value={pdf.subject}
            //defaultValue={`Henderson Electrical Services ${pdfType} - ${refNumber}`}
          />
        </div>
      </div>

      <div className="toolLine">
        <label htmlFor="message">Message</label>
        <div className="message-input-box">
          <MessageEditor
            message={pdf.message}
            onChange={(html) => dispatch({ type: SET_EMAIL_MESSAGE, payload: html })}
          />
        </div>
      </div>

      <div className="toolLine">
        <label htmlFor="attachements">Attachments</label>
        <input
          name="attachments"
          id="attachments"
          type="file"
          // accept="image/*"
          onChange={handleFile}
          multiple="multiple"
          tabIndex={-1}
        />
      </div>

      {isInvoice && (
        <>
          <div className="toolLine">
            <label
              className={
                timesheet?.email &&
                (timesheet?.email?.includes("@gmail") || timesheet?.email?.includes("@google"))
                  ? "potential-reviewer"
                  : ""
              }
              htmlFor="review"
            >
              Review
            </label>
            <input
              type="checkbox"
              name="review"
              label="Review"
              checked={pdf.showReview}
              onChange={() =>
                dispatch({
                  type: TOGGLE_REVIEW,
                  payload: { amount: totalInvoiceCost, addressLine },
                })
              }
            />
          </div>

          <div className="toolLine">
            <label htmlFor="isPaid">All Paid</label>
            <input
              type="checkbox"
              name="isPaid"
              label="isPaid"
              checked={pdf.isPaid}
              onChange={() =>
                dispatch({
                  type: TOGGLE_IS_PAID,
                  payload: { amount: totalInvoiceCost, addressLine },
                })
              }
            />
          </div>

          <div className="toolLine">
            <label htmlFor="both">Both</label>
            <input
              type="checkbox"
              name="both"
              label="both"
              checked={pdf.both}
              onChange={() =>
                dispatch({
                  type: TOGGLE_BOTH,
                  payload: { amount: totalInvoiceCost, addressLine },
                })
              }
            />
          </div>
        </>
      )}
      {/* Send (/sendEmail) bccs this address; Create Draft (/createDraft) never bccs the draft
          itself, but sends a separate records copy here instead (see sendRecordsCopy). */}
      {!threadPickerOpen && (
        <div className="bccBadgeRow">
          <span
            className="bccBadge"
            onMouseEnter={handleBccHover}
            onMouseLeave={() => setBccHovering(false)}
          >
            BCC: {bccLabel}
            {bccHovering && (
              <span className="bccTooltip">
                {bccLoading ? "Loading…" : bccAddress || "Couldn't load address"}
              </span>
            )}
          </span>
        </div>
      )}
      <div className="toolLine">
        <label htmlFor="sendTo">To</label>

        <div className="emailToLine">
          <input
            name="sendTo"
            id="sendTo"
            autoComplete="off"
            type="text"
            value={pdf.sendTo}
            onChange={(e) => {
              dispatch({
                type: SET_EMAIL_SEND_TO,
                payload: { value: e.target.value },
              });
            }}
          />
          {showSpamWarning && (
            <p className="spamWarning">
              <span>warning:</span> emails to this customer address tend to end up in spam
            </p>
          )}
          <div className="presetEmails">
            {sendToBtns.map((preset, i) => {
              // Invoices/Quotes also go out via bcc automatically on Send (not on Create
              // Draft), but adding one directly to "To" is a deliberate, legitimate thing to
              // want (e.g. sending straight to that inbox rather than relying on the bcc) -
              // Gmail de-dupes a recipient that appears in both To and Bcc, so this can't
              // cause a double delivery.
              return (
                <button
                  key={preset.label}
                  disabled={pdf.sending}
                  className={preset.className}
                  onClick={(e) => {
                    dispatch({
                      type: SET_EMAIL_SEND_TO,
                      payload: preset.payload,
                    });
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="toolLine sendSubmit">
        <button
          className="sendBtn"
          disabled={pdf.sending || threadPickerOpen}
          onClick={() => sendPdf(false)}
        >
          Send
        </button>
        {/* {import.meta.env.MODE === "development" && ( */}
        <button
          className="sendBtn"
          disabled={pdf.sending || threadPickerOpen}
          onClick={() => sendPdf(true)}
        >
          Backup
        </button>
        {/* )} */}
      </div>

      {threadPickerOpen && (
        <div
          className="threadPickerPanel"
          style={
            panelPosition
              ? { top: panelPosition.top, right: panelPosition.right, transform: "translateY(-50%)" }
              : undefined
          }
        >
          <div className="threadPickerHeader">
            <h4>Attach to thread</h4>
            <button
              type="button"
              className="closeThreadPicker"
              onClick={() => setThreadPickerOpen(false)}
            >
              ✕
            </button>
          </div>

          {findingThread ? (
            <p className="threadPickerStatus">Searching Gmail…</p>
          ) : (
            <div className="threadPickerOptions">
              <label>
                <input
                  type="radio"
                  name="threadPicker"
                  checked={selectedThreadIndex === null}
                  onChange={() => onSelectThread(null)}
                />
                <span>None (standalone draft)</span>
                {aiAttempted && !aiError && aiSuggestedIndex === null && (
                  <span className="aiSuggestedBadge" title={aiSuggestedReason || undefined}>
                    Suggested
                  </span>
                )}
                {aiAttempted && aiError && (
                  <span
                    className="aiErrorBadge"
                    title="Both AI providers failed to respond, so this defaulted to standalone rather than a real suggestion"
                  >
                    AI check failed
                  </span>
                )}
              </label>
              {threadCandidates.map((thread, i) => (
                <label
                  key={thread.threadId}
                  className={thread.hasDraft ? "threadOptionDisabled" : undefined}
                >
                  <input
                    type="radio"
                    name="threadPicker"
                    checked={selectedThreadIndex === i}
                    disabled={thread.hasDraft}
                    onChange={() => onSelectThread(i)}
                  />
                  {aiAttempted && aiSuggestedIndex === i && (
                    <span className="aiSuggestedBadge" title={aiSuggestedReason || undefined}>
                      Suggested
                    </span>
                  )}
                  {thread.hasDraft && (
                    <a
                      className="threadHasDraftBadge"
                      href={`https://mail.google.com/mail/u/0/#all/${thread.threadId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Gmail behaves oddly with two drafts on the same thread - open the existing draft in Gmail to send/discard it first, or attach a new draft here from Gmail itself instead"
                    >
                      Draft already in thread — open in Gmail
                    </a>
                  )}
                  <span className="threadDate">
                    {dateTimeFormatter(Number(thread.date), "dt_medium")}
                  </span>
                  <span className="threadSubject">{thread.subject}</span>
                  <span className="threadFrom">{thread.from}</span>
                  <span className="threadSnippet">{truncate(thread.snippet, 100)}</span>
                </label>
              ))}
              {threadCandidates.length === 0 && (
                <p className="threadPickerStatus">No related emails found.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PdfEmailBox;
